import { useMutation } from '@tanstack/react-query'
import { createDocumentPageAssemblyGroupIDs } from '@/lib/api/client'
import queryClient from '@/queryClient'
import { useCADPageStore } from '@/state'

import type { DocumentPage } from '@/lib/api/client'

import { QUERY_KEYS as DOCUMENT_PAGES_QUERY_KEYS } from '@/services/queries/document_pages/queryKeys'
import CustomError from '@/lib/api/CustomError'
import { useToast } from '@/components/ui/use-toast'
import { useToggleStepCardsList } from '@/pages/CADPage/components/AssemblyOrderPanel/StepCardList/hooks/useToggleStepCardsList'
import { useCadPageParams } from '@/pages/CADPage/hooks'
import { useOperationSteps } from './useOperationSteps'
import { useKickViewWorker } from '@/services/hooks/view_worker'
import {
  getPartsTablesQuery,
  useUpdateTable,
} from '@/services/hooks/template_attributes'
import { useCADQuery } from '../cads'
import { cadVersionMetadataQuery } from '../cad_versions'

export const useAddPartsToOperation = () => {
  const { toast } = useToast()
  const { documentId } = useCadPageParams()
  const { isViewWorkerProcessing, viewWorker } = useKickViewWorker()
  const { steps } = useOperationSteps()
  const toggleStepCardsList = useToggleStepCardsList()
  const setCadPageState = useCADPageStore((state) => state.setState)
  const activeStep = useCADPageStore((state) => state.operationStep)
  const { mutateAsync: updateTable } = useUpdateTable()
  const { data: cadData } = useCADQuery()

  return useMutation({
    mutationFn: async ({
      cadVersionId,
      documentPageId,
      documentVersionId,
      assemblyGroupIds,
      onSuccess,
    }: {
      cadVersionId: string
      documentPageId: string
      documentVersionId: string
      assemblyGroupIds: string[]
      onSuccess?: () => void
    }) => {
      await createDocumentPageAssemblyGroupIDs(documentPageId, assemblyGroupIds)
      const partsTables = await getPartsTablesQuery(documentPageId)
      const metadata = await cadVersionMetadataQuery({
        cadVersionId,
      })
      const metadataKeys = metadata?.keys || []
      const tablesWithCustomColumns = partsTables.filter(
        (table) => !!table.template_values?.columns?.length,
      )
      const step = steps.find((step) => step.id === documentPageId)
      const assemblyTree = cadData.rawAssemblyTree || { root: '', nodes: [] }
      const currentPartsInOperation = new Set(
        step?.assembly_group_ids
          ?.map(
            (partId) =>
              assemblyTree.nodes.find((node) => node.uuid === partId)
                ?.display_name,
          )
          ?.filter((partName) => !!partName),
      )
      const newPartsInOperation = new Set(
        assemblyGroupIds
          .map(
            (partId) =>
              assemblyTree.nodes.find((node) => node.uuid === partId)
                ?.display_name,
          )
          .filter((partName) => !!partName),
      )
      const updatedPartsInOperation = Array.from(
        new Set([...currentPartsInOperation, ...newPartsInOperation]),
      )

      if (updatedPartsInOperation.length !== currentPartsInOperation.size) {
        const newPartsForRows = new Set(
          [...newPartsInOperation].filter(
            (item) => !currentPartsInOperation.has(item),
          ),
        )
        tablesWithCustomColumns.forEach((table) => {
          const newPartsInOperationArray = Array.from(newPartsForRows)
          const columns = table.template_values?.columns || []
          const rows = table.template_values?.rows || []
          const customRows = rows.slice(
            currentPartsInOperation.size,
            rows.length,
          )
          const prevPartRows = rows.slice(0, currentPartsInOperation.size)
          const updatedPartRows = [...prevPartRows]

          newPartsInOperationArray.forEach((partName) => {
            const newRow = columns.reduce((acc, column) => {
              const columnKeys = Object.keys(column)
              columnKeys.forEach((key) => {
                if (
                  ['id', 'partName', 'quantity'].includes(key) ||
                  metadataKeys.includes(key)
                ) {
                  return
                }
                acc[key] = ''
              })
              return acc
            }, {})
            const insertionIndex = updatedPartsInOperation.indexOf(partName)
            updatedPartRows.splice(insertionIndex, 0, newRow)
          })
          const updatedRows = [...updatedPartRows, ...customRows]

          void updateTable({
            templateAttributeId: table.id as string,
            documentPageId,
            tableData: {
              rows: updatedRows,
            },
          })
        })
      }
      return { cadVersionId, documentPageId, documentVersionId, onSuccess }
    },

    onSuccess: async ({ cadVersionId, documentVersionId, onSuccess }) => {
      await queryClient.invalidateQueries({
        queryKey: [
          DOCUMENT_PAGES_QUERY_KEYS.DOCUMENT_PAGES,
          { documentVersionId },
        ],
        refetchType: 'all',
      })

      if (onSuccess) {
        onSuccess()
      } else {
        if (activeStep) {
          setCadPageState({
            operationStep: {
              documentId,
              stepId: activeStep.stepId,
              isActive: false,
              selectFromCad: false,
            },
          })

          await toggleStepCardsList({
            steps,
            previousSteps: steps,
            documentVersionId,
            isReadOnly: false,
          })
        }

        setCadPageState({ selectedParts: [] })

        if (viewWorker && isViewWorkerProcessing) {
          await viewWorker.kick(cadVersionId, documentVersionId)
        }
      }
    },

    onMutate: ({ assemblyGroupIds, documentPageId, documentVersionId }) => {
      const queryKey = [
        DOCUMENT_PAGES_QUERY_KEYS.DOCUMENT_PAGES,
        { documentVersionId },
      ]

      const previousDocumentPages = (queryClient.getQueryData(queryKey) ||
        []) as DocumentPage[]

      queryClient.setQueryData(queryKey, (old: DocumentPage[]) => {
        if (!old) return []

        return old.map((doc) => {
          if (doc.id === documentPageId) {
            return {
              ...doc,
              documentpageassemblygroupids: [
                ...doc.documentpageassemblygroupids,
                ...assemblyGroupIds.map((assemblyGroupId) => ({
                  assembly_group_id: assemblyGroupId,
                  document_page: documentPageId,
                })),
              ],
            }
          }
          return doc
        })
      })

      return { previousDocumentPages }
    },

    onError: (error, { documentVersionId }, context) => {
      console.error('Failed to add part to operation', error)

      queryClient.setQueryData(
        [DOCUMENT_PAGES_QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
        context?.previousDocumentPages || [],
      )

      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}
